<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            :header-height="40"
            grid-theme="ag-theme-alpine-report"
            class="guest-transactions-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :range-months-limit="12"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-flex align-items-baseline mr-1 mb-1">
                <search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="transactionTypeOptions"
                variant="primary"
            >
                {{ transactionType }}
            </responsive-dropdown>
            <responsive-button
                :href="exportExcelHref"
                :icon="['fas', 'file-excel']"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            />
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                :excelOnly="true"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_YEAR,
    LAST_WEEK,
} from '../../generic/date-range-picker/date-range-picker.vue';
import NoteCellRenderer from './note-cell-renderer.vue';
import { REPORT_GUEST_TRANSACTIONS_ENDPOINT } from '../../../endpoints';
import ReportSummaryBar from '../report-summary-bar.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import { PAGE_ORIENTATION_LABELS, TRANSACTION_TYPE } from '../../../constants';

import ReportSettingsModal, {
    SELECT_INPUT,
    DATE_PERIODS_INPUT,
    MULTI_SELECT_INPUT,
} from '../report-settings/report-settings-modal.vue';

export default {
    name: 'ReportGuestTransactions',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        DateRangePicker,
        SearchMultiselect,
        reportSummaryBar: ReportSummaryBar,
        ResponsiveDropdown,
        ReportSettingsModal,
        noteCellRenderer: NoteCellRenderer,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Guest Transactions',
            tabs: [
                {
                    title: 'Guest Transactions',
                    route: '/app/reports/guest-transactions/',
                    active: true,
                },
            ],
            dateRange: nameToRange(LAST_MONTH),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                LAST_YEAR,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            rowData: [],
            getRowStyle: null,
            pinnedBottomRowData: null,
            windowWidth: 0,
            windowHeight: 0,
            filterProperties: [],
            filterPropertyOptions: [],
            statusBar: null,
            transactionType: TRANSACTION_TYPE[0],
            pageOrientation: PAGE_ORIENTATION_LABELS[0],
            payment_methods: {},
            totalRecords: 0,
            reportSettingsModalId: 'report-settings-modal-id',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        exportExcelHref() {
            let url = '/app/reports/guest-transactions/row-data/excel/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&t_type=${this.transactionType.toUpperCase()}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        columnDefs() {
            return [
                {
                    field: 'index',
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.totalRecords
                            : params.node.rowIndex + 1;
                    },
                    minWidth: 65,
                },
                {
                    field: 'date',
                    headerName: 'Date',
                    minWidth: 100,
                },
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                    minWidth: 100,
                },
                {
                    field: 'invoice_id',
                    headerName: 'Invoice #',
                    minWidth: 100,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    minWidth: 100,
                    cellStyle: ({ value }) => {
                        if (value === 'Cancelled' || value === 'Deleted') {
                            return { color: 'red' };
                        }
                        return null;
                    },
                },
                {
                    field: 'property',
                    headerName: 'Property',
                    minWidth: 110,
                },
                ...Object.keys(this.payment_methods).map((pm_key) => {
                    const pm = this.payment_methods[pm_key];
                    return {
                        field: pm_key,
                        headerName: pm,
                        cellStyle: ({ data, node }) => {
                            const value = data[pm_key] || 0;
                            if (value < 0) {
                                return { color: 'red' };
                            }
                            return null;
                        },
                        type: 'amountColumn',
                        minWidth: 124,
                    };
                }),
                {
                    field: 'total',
                    headerName: 'Total',
                    cellStyle: ({ data }) => {
                        const { total } = data;
                        if (total < 0) {
                            return { color: 'red' };
                        }
                        return null;
                    },
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'sequence',
                    headerName: 'Sequence',
                    minWidth: 100,
                },
                {
                    field: 'transaction_code',
                    headerName: 'Transaction ID',
                    minWidth: 120,
                },
                {
                    field: 'type',
                    headerName: 'Type',
                    minWidth: 80,
                },
                {
                    field: 'card_type',
                    headerName: 'Card Type',
                    minWidth: 100,
                },
                {
                    field: 'cc_or_check_number',
                    headerName: 'CC/Ck #',
                    valueGetter: (data) => {
                        return data.data.cc_or_check_number
                            ? data.data.cc_or_check_number
                            : 'N/A';
                    },
                    minWidth: 80,
                },
                {
                    field: 'notes',
                    headerName: 'Notes',
                    minWidth: 80,
                    autoHeight: true,
                    cellRenderer: 'noteCellRenderer',
                    autoWidthExclude: true,
                },
            ];
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => {
                        this.pageOrientation = PAGE_ORIENTATION_LABELS[0];
                    },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => {
                        this.pageOrientation = PAGE_ORIENTATION_LABELS[1];
                    },
                },
            ];
        },
        transactionTypeOptions() {
            return [
                {
                    label: TRANSACTION_TYPE[0],
                    click: () => {
                        this.transactionType = TRANSACTION_TYPE[0];
                        this.fetchData();
                    },
                },
                {
                    label: TRANSACTION_TYPE[1],
                    click: () => {
                        this.transactionType = TRANSACTION_TYPE[1];
                        this.fetchData();
                    },
                },
                {
                    label: TRANSACTION_TYPE[2],
                    click: () => {
                        this.transactionType = TRANSACTION_TYPE[2];
                        this.fetchData();
                    },
                },
                {
                    label: TRANSACTION_TYPE[3],
                    click: () => {
                        this.transactionType = TRANSACTION_TYPE[3];
                        this.fetchData();
                    },
                },
            ];
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        reportSettings() {
            return {
                name: 'Guest Transactions',
                report_sysname: 'GUEST_TRANSACTIONS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: [
                            YESTERDAY,
                            TODAY,
                            TOMORROW,
                            LAST_WEEK,
                            THIS_WEEK,
                            NEXT_WEEK,
                            LAST_MONTH,
                            THIS_MONTH,
                            NEXT_MONTH,
                            THIS_YEAR,
                            LAST_YEAR,
                        ],
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        name: 'properties',
                        label: 'Only for Properties',
                        options: this.filterPropertyOptions,
                        placeholder: 'All Properties'
                    },
                    {
                        type: SELECT_INPUT,
                        value: 'ALL',
                        name: 't_type',
                        options: [
                            {
                                text: TRANSACTION_TYPE[0],
                                value: TRANSACTION_TYPE[0].toUpperCase(),
                            },
                            {
                                text: TRANSACTION_TYPE[1],
                                value: TRANSACTION_TYPE[1].toUpperCase(),
                            },
                            {
                                text: TRANSACTION_TYPE[2],
                                value: TRANSACTION_TYPE[2].toUpperCase(),
                            },
                            {
                                text: TRANSACTION_TYPE[3],
                                value: TRANSACTION_TYPE[3].toUpperCase(),
                            },
                        ],
                        label: 'Transaction Type',
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchProperties();
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        grid.gridOptions.enableBrowserTooltips = true;
        // grid.gridOptions.tooltipShowDelay = 0;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.pinnedBottomRowData = [
            {
                index: 0,
                total: 0,
            },
        ];
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_GUEST_TRANSACTIONS_ENDPOINT('row-data'),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            properties: this.filterProperties.join(','),
                            t_type: this.transactionType.toUpperCase(),
                        },
                    }
                );
                const { transactions, total, payment_table, payment_methods } =
                    data;
                this.payment_methods = payment_methods;
                this.rowData = [
                    ...transactions.map((transaction, index) => ({
                        id: index,
                        ...transaction,
                        ...transaction.payment_table,
                    })),
                ];
                this.totalRecords = this.rowData.length;
                const statusBar = {
                    index: this.rowData.length,
                    total,
                    ...payment_table,
                };
                this.pinnedBottomRowData = [{ ...statusBar }];
            } catch (e) {
                console.error(e);
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            const { data } = await API.get(
                REPORT_GUEST_TRANSACTIONS_ENDPOINT('properties')
            );
            this.filterPropertyOptions = data.map((item) => ({
                value: item.property_id,
                text: item.name,
                disabled: item.disabled,
            }));
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
    watch: {
        filterProperties: {
            deep: true,
            handler() {
                this.fetchData();
            },
        },
    },
};
</script>